
import { useState } from 'react';

interface UploadedFile {
  id: string;
  name: string;
  title: string;
  author: string;
  publishDate: string;
  description: string;
  category: string;
  size: number;
  uploadDate: string;
  path: string;
  tags: string[];
  downloadCount: number;
}

export default function Admin() {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([
    {
      id: '1',
      name: '日本企業のAI導入実態調査2024.pdf',
      title: '日本企業のAI導入実態調査 2024',
      author: '経済産業省研究所',
      publishDate: '2024年3月',
      description: '大企業と中小企業のAI導入格差を詳細に分析した研究',
      category: '企業調査',
      size: 2.3,
      uploadDate: '2024-03-15',
      path: '/documents/日本企業のAI導入実態調査2024.pdf',
      tags: ['AI導入', '企業調査', '競争力'],
      downloadCount: 1247
    },
    {
      id: '2',
      name: '世代別デジタル技術利用状況調査.pdf',
      title: '世代別デジタル技術利用状況調査',
      author: '総務省統計局',
      publishDate: '2024年2月',
      description: '世代別のAI・デジタル技術利用状況を詳細に分析',
      category: '統計調査',
      size: 1.8,
      uploadDate: '2024-02-15',
      path: '/documents/世代別デジタル技術利用状況調査.pdf',
      tags: ['世代格差', 'デジタル技術'],
      downloadCount: 892
    },
    {
      id: '3',
      name: 'AI時代の感覚重視教育論.pdf',
      title: 'AI時代の感覚重視教育論',
      author: '教育理論研究所',
      publishDate: '2024年3月',
      description: '人間固有の感覚と直感を重視した新しい教育パラダイムを提案',
      category: '教育理論',
      size: 3.2,
      uploadDate: '2024-03-20',
      path: '/documents/AI時代の感覚重視教育論.pdf',
      tags: ['感覚教育', '教育理論'],
      downloadCount: 1823
    }
  ]);
  const [uploading, setUploading] = useState(false);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<UploadedFile>>({});

  const categories = ['企業調査', '統計調査', '国際比較', '教育研究', '教育理論', 'その他'];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const pdfFiles = files.filter(file => file.type === 'application/pdf');

    if (pdfFiles.length > 0) {
      handleFiles(pdfFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length > 0) {
      handleFiles(pdfFiles);
    }
  };

  const handleFiles = async (files: File[]) => {
    setUploading(true);

    for (const file of files) {
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newFile: UploadedFile = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        name: file.name,
        title: file.name.replace('.pdf', ''),
        author: '著者未設定',
        publishDate: new Date().toISOString().split('T')[0],
        description: '説明未設定',
        category: 'その他',
        size: parseFloat((file.size / (1024 * 1024)).toFixed(1)),
        uploadDate: new Date().toISOString().split('T')[0],
        path: `/documents/${file.name}`,
        tags: [],
        downloadCount: 0
      };

      setUploadedFiles(prev => [...prev, newFile]);
    }

    setUploading(false);
  };

  const deleteFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const openEditModal = (file: UploadedFile) => {
    setEditForm(file);
    setShowEditModal(file.id);
  };

  const closeEditModal = () => {
    setShowEditModal(null);
    setEditForm({});
  };

  const saveEdit = () => {
    if (!showEditModal || !editForm) return;

    setUploadedFiles(prev => prev.map(file => 
      file.id === showEditModal ? { ...file, ...editForm } : file
    ));
    closeEditModal();
  };

  const formatFileSize = (size: number) => {
    return `${size}MB`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">PDF研究論文管理</h1>
          <p className="text-gray-600">研究論文のPDFファイルをアップロード・管理し、論文ページに自動反映されます</p>
        </div>

        {/* ナビゲーション */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            <button 
              onClick={() => {
                if (typeof window.REACT_APP_NAVIGATE === 'function') {
                  window.REACT_APP_NAVIGATE('/research-papers');
                } else {
                  console.error('REACT_APP_NAVIGATE is not defined or not a function');
                }
              }}
              className="bg-blue-100 text-blue-700 px-6 py-3 rounded-xl font-medium hover:bg-blue-200 transition-colors inline-flex items-center gap-2"
            >
              <i className="ri-file-text-line"></i>
              論文ページを確認
            </button>
            
            <button 
              onClick={() => {
                if (typeof window.REACT_APP_NAVIGATE === 'function') {
                  window.REACT_APP_NAVIGATE('/');
                } else {
                  console.error('REACT_APP_NAVIGATE is not defined or not a function');
                }
              }}
              className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors inline-flex items-center gap-2"
            >
              <i className="ri-home-line"></i>
              ホームページ
            </button>
          </div>
        </div>

        {/* ドラッグ&ドロップエリア */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-8">
          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-upload-cloud-line text-3xl text-white"></i>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              研究論文のPDFファイルをアップロード
            </h3>
            <p className="text-gray-600 mb-6">
              ファイルをドラッグ&ドロップするか、ボタンで選択してください
            </p>
            
            <label className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg cursor-pointer">
              <i className="ri-folder-open-line text-xl"></i>
              <span>ファイルを選択</span>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>

            {uploading && (
              <div className="mt-6">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-blue-600 font-medium">アップロード中...</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* アップロード済みファイル一覧 */}
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">管理中の研究論文</h2>
            <div className="text-sm text-gray-500">
              合計 {uploadedFiles.length} ファイル
            </div>
          </div>

          {uploadedFiles.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-gray-500">まだファイルがアップロードされていません</p>
            </div>
          ) : (
            <div className="space-y-6">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-pdf-line text-2xl text-white"></i>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                          {file.category}
                        </span>
                        <span className="text-gray-500 text-sm">{file.publishDate}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{file.title}</h3>
                      <p className="text-gray-600 mb-1">著者: {file.author}</p>
                      <p className="text-gray-700 mb-3 leading-relaxed">{file.description}</p>
                      
                      {/* タグ */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {file.tags.map((tag, index) => (
                          <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-lg text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatFileSize(file.size)}</span>
                        <span>•</span>
                        <span>{file.downloadCount} ダウンロード</span>
                        <span>•</span>
                        <span>アップロード: {file.uploadDate}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => openEditModal(file)}
                        className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
                        title="編集"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      
                      <a
                        href={file.path}
                        download={file.name}
                        className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors"
                        title="ダウンロード"
                      >
                        <i className="ri-download-line"></i>
                      </a>
                      
                      <button
                        onClick={() => navigator.clipboard.writeText(window.location.origin + file.path)}
                        className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center hover:bg-purple-200 transition-colors"
                        title="リンクをコピー"
                      >
                        <i className="ri-link-line"></i>
                      </button>
                      
                      <button
                        onClick={() => deleteFile(file.id)}
                        className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center hover:bg-red-200 transition-colors"
                        title="削除"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 編集モーダル */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">論文情報を編集</h3>
                  <button 
                    onClick={closeEditModal}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">論文タイトル</label>
                    <input
                      type="text"
                      value={editForm.title || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">著者</label>
                    <input
                      type="text"
                      value={editForm.author || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, author: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">発表日</label>
                      <input
                        type="text"
                        placeholder="2024年3月"
                        value={editForm.publishDate || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, publishDate: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">カテゴリ</label>
                      <select
                        value={editForm.category || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
                      >
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">説明</label>
                    <textarea
                      rows={4}
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">タグ（カンマ区切り）</label>
                    <input
                      type="text"
                      placeholder="AI導入, 企業調査, 競争力"
                      value={editForm.tags?.join(', ') || ''}
                      onChange={(e) => setEditForm(prev => ({ 
                        ...prev, 
                        tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag) 
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-4 mt-8">
                  <button
                    onClick={saveEdit}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-700 text-white py-3 rounded-lg font-bold hover:from-blue-700 hover:to-purple-800 transition-all"
                  >
                    保存
                  </button>
                  <button
                    onClick={closeEditModal}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 使用方法の説明 */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8 mt-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">管理システムの使用方法</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">1. PDFアップロード</h4>
              <p className="text-gray-600 text-sm mb-3">
                研究論文のPDFファイルをドラッグ&ドロップまたはファイル選択でアップロード
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">2. 論文情報編集</h4>
              <p className="text-gray-600 text-sm mb-3">
                タイトル、著者、説明、カテゴリ、タグなどの詳細情報を編集
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">3. 自動反映</h4>
              <p className="text-gray-600 text-sm mb-3">
                編集した情報は研究論文ページに自動的に反映され、検索・フィルタリング可能
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">4. ダウンロード管理</h4>
              <p className="text-gray-600 text-sm mb-3">
                ダウンロード数の追跡、リンク共有、ファイル削除などの管理機能
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
